import { uniqWith, evolve } from 'rambda';

import {
  UserLocationInfo,
  getOpeningTimeInfo,
  getCurrentTime,
  PartnerStatus,
  PartnerDescription,
  TLD,
  AlgoliaIndiceName,
  LocalDealerData,
  MAX_PARTNERS_TO_ROTATE,
} from '@packages/common';

import { SearchBase } from './searchBase';

const BASIC_PARTNER_SEARCH_RADIUS = 50000;
const NONBASIC_PARTNER_SEARCH_RADIUS = 100000;

const attributesToRetrieveWithPosition: (keyof LocalDealerData)[] = [
  'city',
  '_geoloc',
  'houseNumber',
  'merchantLogo',
  'openingHours',
  'merchantPartnerName',
  'zip',
  'street',
  'objectID',
  'showMerchantAd',
  'merchantPartnerStatus',
  'merchantPartnerID',
  'merchantPartnerStoreID',
  'url',
  'objectID',
  'partnerUrl',
  'backgroundImage',
];

export interface Response {
  hits: PartnerDescription[];
}

export const convertToPartnerStatus = (input: string): PartnerStatus => {
  return (input ?? PartnerStatus.basic).toLowerCase() as PartnerStatus;
};

export const mapRawDealerDataToDisplayableDealer = (merchants: LocalDealerData[], tld: TLD) => {
  if (!merchants) {
    return [];
  }

  return merchants
    .filter((hit) => hit)
    .map((hit) => {
      const time = getOpeningTimeInfo(tld, getCurrentTime(), hit.openingHours);

      return {
        ...hit,
        key: hit.objectID,
        coords: { lat: Number(hit._geoloc.lat), lng: Number(hit._geoloc.lng) },
        imageUrl: hit.merchantLogo,
        isOpenNow: !!(time && time.isOpenNow),
        name: hit.merchantPartnerName,
        nextOpenTime: time,
        merchantPartnerStatus: convertToPartnerStatus(hit.merchantPartnerStatus),
      };
    });
};

// Search should be done client-side only, ssr requires additional headers
export const localMerchantsWithAdsSearchByIp = (searchBase: SearchBase, tld: TLD) => async () => {
  const merchantsWithAds = await searchBase<LocalDealerData>(AlgoliaIndiceName.LOCAL_MERCHANTS, {
    aroundRadius: NONBASIC_PARTNER_SEARCH_RADIUS,
    hitsPerPage: MAX_PARTNERS_TO_ROTATE,
    aroundLatLngViaIP: true,
    filters: 'showMerchantAd=1',
  });

  if (!merchantsWithAds) {
    return null;
  }

  return mapRawDealerDataToDisplayableDealer(merchantsWithAds.hits, tld);
};

export const localMerchantsSearchByPosition = (searchBase: SearchBase, tld: TLD) => async (pos: UserLocationInfo) => {
  const algoliaIndex = AlgoliaIndiceName.LOCAL_MERCHANTS;

  const nonBasicMerchants = await searchBase<LocalDealerData>(algoliaIndex, {
    aroundRadius: NONBASIC_PARTNER_SEARCH_RADIUS,
    aroundLatLng: `${pos.coords.lat}, ${pos.coords.lng}`,
    attributesToRetrieve: attributesToRetrieveWithPosition,
    filters: 'NOT merchantPartnerStatus:basic AND NOT merchantPartnerStatus:free',
  });

  const basicMerchants = await searchBase<LocalDealerData>(algoliaIndex, {
    aroundRadius: BASIC_PARTNER_SEARCH_RADIUS,
    aroundLatLng: `${pos.coords.lat}, ${pos.coords.lng}`,
    attributesToRetrieve: attributesToRetrieveWithPosition,
    filters: 'merchantPartnerStatus:basic OR merchantPartnerStatus:free',
  });

  const merchants: LocalDealerData[] = uniqWith(
    (a, b) => a?.objectID === b?.objectID,
    [...(nonBasicMerchants || { hits: [] }).hits, ...(basicMerchants || { hits: [] }).hits],
  ) as LocalDealerData[];

  return mapRawDealerDataToDisplayableDealer(merchants, tld);
};

const attributesToRetrieveWithId = [
  'merchantInfo',
  'merchantPartnerName',
  '_geoloc',
  'street',
  'houseNumber',
  'zip',
  'city',
  'phoneNumber',
  'partnerUrl',
  'openingHours',
  'merchantImages',
  'merchantLogo',
  'merchantPartnerStatus',
];

export const localDealerSearchById =
  (searchBase: SearchBase) =>
  async (dealerObjectId: string): Promise<PartnerDescription> => {
    const algoliaIndex = AlgoliaIndiceName.LOCAL_MERCHANTS;
    const response = await searchBase(algoliaIndex, {
      attributesToRetrieve: attributesToRetrieveWithId,
      hitsPerPage: 1,
      filters: `objectID:${dealerObjectId}`,
    });

    let dealer: PartnerDescription = (response?.hits?.[0] ?? {}) as PartnerDescription;

    if (dealer) {
      dealer = evolve({ merchantPartnerStatus: convertToPartnerStatus }, dealer);
    }

    return dealer;
  };
